/**
 * Voice-Leading Diff (in-editor) — pure analyzer for the smoothest voice leading
 * between two adjacent chart chords.
 *
 * Chart chords are pitch-class SYMBOLS (no octave, no voice assignment), so there
 * is no "the" voice leading to display. What we compute is the smoothest POSSIBLE
 * connection: common tones held, then the remaining voices assigned by the
 * permutation that minimises total semitone motion. This is the voice-level
 * companion to `describeTransition` (root motion) — same honest-empty-space rule:
 * an unanalyzable chord returns `null` and the surface hides.
 *
 * All music-theory decisions are Bowie-locked.
 * Spec: docs/superpowers/specs/2026-06-06-voice-leading-diff-model-spec.md
 */

import type { NoteName } from '@tunasong/schemas'
import { ChordLib } from '../chord/index.js'
import type { Chord } from '../chord/chord-types.js'
import { NoteLib } from '../note/index.js'

export type VoiceMotionKind = 'step' | 'leap'
export type VoiceDirection = 'up' | 'down'
export type VoiceLeadingSmoothness = 'smooth' | 'mixed' | 'angular'

export interface VoiceMove {
  from: NoteName
  to: NoteName
  motion: VoiceMotionKind
  direction: VoiceDirection
}

export interface VoiceLeadingDiff {
  /** Common tones held across the pair (motion 0). */
  heldCount: number
  /** Moved voices, in the order their `from` pitch class appears chromatically. */
  moves: VoiceMove[]
  /** Surplus voices that join in the larger chord (triad → 7th). */
  added?: NoteName[]
  /** Surplus voices that drop out in the smaller chord (7th → triad). */
  dropped?: NoteName[]
  /** One-glance summary bucket (§4). */
  smoothness: VoiceLeadingSmoothness
}

/** Internal: a pitch class carried alongside its chromatic index (0–11). */
interface Pc {
  name: NoteName
  index: number
}

/** Shorter signed semitone distance between two pitch classes (+up / −down, range −6..+6). */
const shorterDirectedDistance = (from: number, to: number): number => {
  const raw = ((to - from) % 12 + 12) % 12
  return raw > 6 ? raw - 12 : raw
}

const toMove = (from: Pc, to: Pc): VoiceMove => {
  const signed = shorterDirectedDistance(from.index, to.index)
  const semitones = Math.abs(signed)
  return {
    from: from.name,
    to: to.name,
    // Reuse the VLL engine's threshold (1–2 = step, ≥3 = leap), applied in
    // pitch-class space because chart chords carry no octave.
    motion: semitones <= 2 ? 'step' : 'leap',
    direction: signed >= 0 ? 'up' : 'down',
  }
}

/** Reduce a chord to its distinct pitch classes, dropping any without a chromatic index. */
const toPitchClasses = (chord: Chord): Pc[] => {
  const seen = new Set<number>()
  const pcs: Pc[] = []
  for (const name of ChordLib.getNotes(chord)) {
    const index = NoteLib.getNoteChromaticIndex(name)
    if (index < 0 || seen.has(index)) {continue}
    seen.add(index)
    pcs.push({ name, index })
  }
  return pcs
}

/** All permutations of an array (≤4 voices, so ≤24 — cheap brute force). */
const permutations = <T>(items: T[]): T[][] => {
  if (items.length <= 1) {return [items]}
  return items.flatMap((item, i) =>
    permutations([...items.slice(0, i), ...items.slice(i + 1)]).map(rest => [item, ...rest])
  )
}

/** All k-sized combinations of an array, preserving order. */
const combinations = <T>(items: T[], k: number): T[][] => {
  if (k === 0) {return [[]]}
  if (items.length < k) {return []}
  const [head, ...tail] = items
  return [...combinations(tail, k - 1).map(c => [head, ...c]), ...combinations(tail, k)]
}

interface Assignment {
  moves: VoiceMove[]
  totalMotion: number
  stepCount: number
}

/**
 * Best assignment of `from` pitch classes onto `to` pitch classes (equal length).
 * Minimises total semitone motion; ties broken toward more stepwise motion.
 */
const bestEqualAssignment = (from: Pc[], to: Pc[]): Assignment => {
  let best: Assignment | null = null
  for (const perm of permutations(to)) {
    const moves = from.map((f, i) => toMove(f, perm[i]))
    const totalMotion = moves.reduce((sum, m) => sum + motionSemitones(m), 0)
    const stepCount = moves.filter(m => m.motion === 'step').length
    if (!best || totalMotion < best.totalMotion || (totalMotion === best.totalMotion && stepCount > best.stepCount)) {
      best = { moves, totalMotion, stepCount }
    }
  }
  // from.length >= 1 guarantees a result.
  return best ?? { moves: [], totalMotion: 0, stepCount: 0 }
}

/** Absolute semitone span of a move, derived from its endpoints (avoids re-deriving distance). */
const motionSemitones = (move: VoiceMove): number => {
  const fromIndex = NoteLib.getNoteChromaticIndex(move.from)
  const toIndex = NoteLib.getNoteChromaticIndex(move.to)
  return Math.abs(shorterDirectedDistance(fromIndex, toIndex))
}

const classifySmoothness = (heldCount: number, moves: VoiceMove[]): VoiceLeadingSmoothness => {
  const leapCount = moves.filter(m => m.motion === 'leap').length
  // smooth — ≥1 common tone AND no leaps.
  if (heldCount >= 1 && leapCount === 0) {return 'smooth'}
  // angular — ≥2 leaps, or any leap with ≤1 common tone.
  if (leapCount >= 2) {return 'angular'}
  if (leapCount === 1 && heldCount <= 1) {
    // mixed — exactly 1 common tone with a single leap; otherwise angular.
    return heldCount === 1 ? 'mixed' : 'angular'
  }
  // mixed — no common tone but all stepwise.
  return 'mixed'
}

export const voiceLeadingDiff = (previous: Chord, current: Chord): VoiceLeadingDiff | null => {
  const prevPcs = toPitchClasses(previous)
  const currPcs = toPitchClasses(current)
  if (prevPcs.length === 0 || currPcs.length === 0) {return null}

  // Common tones are held voices (motion 0) — the unambiguous signal, surfaced first.
  const currIndices = new Set(currPcs.map(p => p.index))
  const prevIndices = new Set(prevPcs.map(p => p.index))
  const heldCount = prevPcs.filter(p => currIndices.has(p.index)).length

  const prevRemaining = prevPcs.filter(p => !currIndices.has(p.index))
  const currRemaining = currPcs.filter(p => !prevIndices.has(p.index))

  let moves: VoiceMove[] = []
  let added: NoteName[] | undefined
  let dropped: NoteName[] | undefined

  if (prevRemaining.length === currRemaining.length) {
    moves = bestEqualAssignment(prevRemaining, currRemaining).moves
  } else if (prevRemaining.length < currRemaining.length) {
    // Size mismatch, larger current: match prev to its nearest current voices;
    // the surplus current voices join as `added`.
    const { moves: matchedMoves, chosen } = bestSubsetAssignment(prevRemaining, currRemaining)
    moves = matchedMoves
    added = currRemaining.filter(p => !chosen.has(p.index)).map(p => p.name)
  } else {
    // Size mismatch, smaller current: match current to its nearest prev voices;
    // the surplus prev voices drop out as `dropped`.
    const { moves: matchedMoves, chosen } = bestSubsetAssignment(currRemaining, prevRemaining)
    // matchedMoves go current→prev; flip them back to prev→current direction.
    moves = matchedMoves.map(m => toMoveFromNames(m.to, m.from))
    dropped = prevRemaining.filter(p => !chosen.has(p.index)).map(p => p.name)
  }

  return {
    heldCount,
    moves,
    ...(added && added.length > 0 ? { added } : {}),
    ...(dropped && dropped.length > 0 ? { dropped } : {}),
    smoothness: classifySmoothness(heldCount, moves),
  }
}

/** Rebuild a move from raw pitch-class names (used to flip a current→prev match). */
const toMoveFromNames = (from: NoteName, to: NoteName): VoiceMove => {
  const fromPc: Pc = { name: from, index: NoteLib.getNoteChromaticIndex(from) }
  const toPc: Pc = { name: to, index: NoteLib.getNoteChromaticIndex(to) }
  return toMove(fromPc, toPc)
}

interface SubsetAssignment {
  moves: VoiceMove[]
  chosen: Set<number>
}

/**
 * Match every `small` pitch class onto a distinct subset of the `large` pool by
 * the choice + permutation minimising total semitone motion (ties → more steps).
 */
const bestSubsetAssignment = (small: Pc[], large: Pc[]): SubsetAssignment => {
  let best: { moves: VoiceMove[]; chosen: Pc[]; total: number; steps: number } | null = null
  for (const chosen of combinations(large, small.length)) {
    for (const perm of permutations(chosen)) {
      const moves = small.map((s, i) => toMove(s, perm[i]))
      const total = moves.reduce((sum, m) => sum + motionSemitones(m), 0)
      const steps = moves.filter(m => m.motion === 'step').length
      if (!best || total < best.total || (total === best.total && steps > best.steps)) {
        best = { moves, chosen, total, steps }
      }
    }
  }
  if (!best) {return { moves: [], chosen: new Set() }}
  return { moves: best.moves, chosen: new Set(best.chosen.map(p => p.index)) }
}
